import httpClient from 'services/httpClient';

/**
 * Center management API (`/centers` + `/platform/centers`).
 * Center-scoped roles live here too; the roleService resolves to these paths
 * automatically for a center-pinned user (see rolesEndpoint).
 */
export const centerService = {
  list: (params) => httpClient.get('/centers', { params }),
  // Super-admin Centers directory detail (CenterView.jsx) — name/website/
  // status/stats/admin info. NOT the same as getProfile below (that's the
  // center's own editable settings, a different shape/endpoint).
  get: (id) => httpClient.get(`/centers/${id}`),
  getProfile: (id) => httpClient.get(`/centers/${id}/profile`),
  // "Verify Link" (Centers module): confirm the center's unique apply-link slug
  // and flip its verified status.
  verifyLink: (id) => httpClient.post(`/centers/${id}/verify-link`),
  // Center onboarding — creates the center and its admin account in one call.
  // A super admin's centers always land in their own organization. The master
  // may additionally send `organization_id` (Onboarding → Center → "inside an
  // organization"); omitted, a master-created center falls back to the reserved
  // Master organization. The field is ignored for anyone else.
  create: (payload) => httpClient.post('/platform/centers/register', payload),
  // Edit core center details (name/location live on the center profile).
  update: (id, payload) => httpClient.put(`/centers/${id}/profile`, payload),
  // Stores the image and returns { logo_url }; the URL (+ `logo_shape`) is
  // written onto the center by the next `update()` call, like the
  // organization logo.
  uploadLogo: (id, file, shape = 'square') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('shape', shape);
    return httpClient.post(`/centers/${id}/logo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Approval workflow.
  pending: (params) => httpClient.get('/platform/centers/pending', { params }),
  approved: (params) => httpClient.get('/platform/centers/approved', { params }),
  approve: (id) => httpClient.post(`/platform/centers/${id}/approve`),
  reject: (id, reason) => httpClient.post(`/platform/centers/${id}/reject`, { reason }),

  // Settings.
  getSettings: (id) => httpClient.get(`/centers/${id}/settings`),
  updateSettings: (id, payload) => httpClient.put(`/centers/${id}/settings`, payload),

  // Notification preferences — per category, whether in-app and/or email is
  // enabled for this center. GET always returns the full category taxonomy
  // (defaults overlaid with anything stored), so the UI never has to
  // hardcode the list.
  getNotificationPreferences: (id) => httpClient.get(`/centers/${id}/notification-preferences`),
  updateNotificationPreferences: (id, preferences) =>
    httpClient.put(`/centers/${id}/notification-preferences`, { preferences }),

  // Center-scoped roles.
  listRoles: (id, params) => httpClient.get(`/centers/${id}/roles`, { params }),
  createRole: (id, payload) => httpClient.post(`/centers/${id}/roles`, payload),
  getRole: (id, roleId) => httpClient.get(`/centers/${id}/roles/${roleId}`),
  updateRole: (id, roleId, payload) => httpClient.put(`/centers/${id}/roles/${roleId}`, payload),
  removeRole: (id, roleId) => httpClient.delete(`/centers/${id}/roles/${roleId}`),
  roleUsers: (id, roleId, params) =>
    httpClient.get(`/centers/${id}/roles/${roleId}/users`, { params }),

  // Members.
  getMembers: (id, params) => httpClient.get(`/centers/${id}/members`, { params }),
  updateMemberRole: (id, userId, payload) =>
    httpClient.put(`/centers/${id}/members/${userId}/role`, payload),
  removeMember: (id, userId) => httpClient.delete(`/centers/${id}/members/${userId}`),

  // Analytics.
  analytics: (id) => httpClient.get(`/api/platform/centers/${id}/analytics`),
  platformOverview: () => httpClient.get('/api/platform/analytics/overview'),
};

export default centerService;
